import { useMemo, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getTrans } from 'modules/i18n';
import { textFor } from '../localizedText';
import {
  signalMarker,
  signalOptionDescription,
  signalOptionLabel,
  signalOptionRow,
  signalScaleCell,
  signalTray,
  signalTraySegment
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  RatingLevel,
  RatingStyle,
  readRatingAnswer,
  readRatingConfig
} from './rating';
import { BlockRendererProps, questionIdOf } from './types';

/**
 * `rating@1`: one level from a small ordered scale.
 *
 * A radio group, because that is what it is — exactly one answer, arrow keys move between levels,
 * and assistive technology announces "2 of 3, selected". The respondent sends only the level KEY;
 * the position is derived server-side from the frozen scale, so nothing here computes or transmits a
 * number. Sending one would be refused outright.
 *
 * The three styles are not decoration, and picking between them is a real choice:
 *
 * - `LABELS` stacks the levels vertically, because words need a full line and a row of equal-width
 *   cells would either truncate "Sehr sicher" or size itself to the longest one.
 * - `NUMERIC` and `STARS` lay the scale out horizontally, which is what makes it read as a
 *   *continuum* rather than a list — and that only works when the cells carry no words.
 *
 * Stars additionally fill cumulatively, matching the convention people already know: choosing the
 * fourth star lights the first four. That convention is only honest where more is genuinely better,
 * which is why it is an operator's choice per question rather than the default.
 */
export const RatingBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const { levels, style } = useMemo(() => readRatingConfig(block), [block]);
  const selectedKey = readRatingAnswer(answer)?.levelKey;
  const selectedIndex = levels.findIndex(level => level.key === selectedKey);

  const [focusIndex, setFocusIndex] = useState(() =>
    selectedIndex >= 0 ? selectedIndex : 0
  );
  const cellRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const questionId = questionIdOf(block);

  const select = (level: RatingLevel) => onChange({ levelKey: level.key });

  const moveFocus = (delta: number) => {
    if (!levels.length) {
      return;
    }
    const next = (focusIndex + delta + levels.length) % levels.length;
    setFocusIndex(next);
    cellRefs.current[next]?.focus();
    // Arrow keys select as they move — native radio-group behaviour, and on a scale it is also the
    // fastest way to feel the distance between two levels.
    select(levels[next]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      moveFocus(1);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      moveFocus(-1);
    }
  };

  const horizontal = style === 'NUMERIC' || style === 'STARS';
  /**
   * A short scale becomes ONE tray, which is what makes it read as a range rather than as several
   * adjacent buttons. Past four levels the segments get too narrow for their labels, so it falls
   * back to separate cells — the tray only helps while you can still read what is in it.
   */
  const compact = horizontal && levels.length <= 4;

  const containerSx = horizontalContainerSx(horizontal, compact);
  const cellSx = (isSelected: boolean) => {
    if (!horizontal) {
      return signalOptionRow(isSelected);
    }
    return compact
      ? signalTraySegment(isSelected)
      : signalScaleCell(isSelected);
  };

  return (
    <Stack spacing={3}>
      {/* A scale looks like a row of buttons until you know it is ordered; the hint says which of
          the two it is before anyone taps to find out. */}
      <BlockHeading
        block={block}
        locale={locale}
        hint={getTrans('guestForm.rating.hint')}
      />

      <Box
        role="radiogroup"
        aria-labelledby={questionId}
        aria-required={block.required}
        onKeyDown={handleKeyDown}
        sx={containerSx}
      >
        {levels.map((level, index) => {
          const isSelected = level.key === selectedKey;
          // A star is "at or below the chosen one", which is why it reads from the selection rather
          // than from this cell alone.
          const isLit = style === 'STARS' && selectedIndex >= index;
          const label = textFor(level.label, locale);
          const description = textFor(level.description, locale);

          return (
            <ButtonBase
              key={level.key}
              ref={element => {
                cellRefs.current[index] = element;
              }}
              role="radio"
              aria-checked={isSelected}
              // The label always names the level, even when the cell shows a number or a star:
              // "3 of 5" tells a screen-reader user nothing about what they are choosing.
              aria-label={label}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => {
                setFocusIndex(index);
                select(level);
              }}
              data-testid={`form-level-${level.key}`}
              sx={cellSx(isSelected)}
            >
              {horizontal ? (
                <Stack alignItems="center" spacing={0.5} sx={{ px: 0.5 }}>
                  <ScaleMark
                    style={style}
                    lit={isLit}
                    selected={isSelected}
                    position={index + 1}
                  />
                  {/* The wording still appears under the mark. A bare 1–5 scale means whatever the
                      respondent assumes it means, which is not an answer anyone can act on. */}
                  <Typography
                    variant="caption"
                    color={isSelected ? 'primary.main' : 'text.secondary'}
                    sx={{ textAlign: 'center', lineHeight: 1.2 }}
                  >
                    {label}
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Box aria-hidden="true" sx={signalMarker(isSelected)}>
                    <CheckIcon />
                  </Box>
                  <Box sx={signalOptionLabel}>
                    {label}
                    {description && (
                      <Box sx={signalOptionDescription}>{description}</Box>
                    )}
                  </Box>
                </>
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
};

/**
 * The mark inside a horizontal cell: a filled or empty star, or the position.
 *
 * Extracted because the alternative is a ternary inside a ternary, and a reader trying to answer
 * "what does the fourth cell show when nothing is selected" should not have to unpick two levels of
 * nesting to find out.
 */
const ScaleMark = ({
  style,
  lit,
  selected,
  position
}: {
  style: RatingStyle;
  lit: boolean;
  selected: boolean;
  position: number;
}) => {
  if (style === 'STARS') {
    return lit ? (
      <StarIcon fontSize="small" aria-hidden="true" />
    ) : (
      <StarBorderIcon fontSize="small" aria-hidden="true" />
    );
  }
  return (
    <Typography
      variant="body1"
      fontWeight={selected ? 600 : 400}
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {position}
    </Typography>
  );
};

/** How the levels are laid out: one tray, a row of cells, or a stack. */
const horizontalContainerSx = (
  horizontal: boolean,
  compact: boolean
): SxProps<Theme> => {
  if (!horizontal) {
    return { display: 'flex', flexDirection: 'column', gap: '9px' };
  }
  return compact
    ? signalTray
    : { display: 'flex', flexDirection: 'row', gap: 1 };
};
